import { ALL_PERMISSIONS, permissionsFor, type Permission, type Resource } from "./statement";

/* Stored by Better Auth (`auth.member.role`): the owner runs all of it, staff is
   everybody else. Running a building is the facility `admin` role on its row. */
export const ORG_ROLES = ["owner", "staff"] as const;
export type OrgRole = (typeof ORG_ROLES)[number];

/** `app.user_facilities.role`: `admin` runs the building, `staff` works at it. */
export const FACILITY_ROLES = ["admin", "staff"] as const;
export type FacilityRole = (typeof FACILITY_ROLES)[number];

function grant(...groups: Array<[Resource, (readonly string[])?]>): Permission[] {
  return groups.flatMap(([resource, actions]) => permissionsFor(resource, actions));
}

/** Every login needs: dashboard, `settings:read`, organization and building names. */
export const ESSENTIALS: Array<[Resource, readonly string[]]> = [
  ["dashboard", ["read"]],
  ["settings", ["read"]],
  ["organization", ["read"]],
  ["facility", ["read"]],
];

/** The built-in Staff role grants nothing of its own: everything comes from native or custom roles
 * somebody chose, never a baseline nobody can see. The old baseline survives as the "General staff"
 * template. */
export const STAFF_ESSENTIALS: Permission[] = grant(...ESSENTIALS);

const PLATFORM_ONLY: Permission[] = permissionsFor("platform");
const BILLING: Permission[] = permissionsFor("organization", ["manage-billing"]);

const ADMIN: Permission[] = ALL_PERMISSIONS.filter(
  (p) => !PLATFORM_ONLY.includes(p) && !BILLING.includes(p),
);

/** Owner signed the organization up: everything, billing included, at every building. Staff is only
 * the essentials; what staff may do is decided building by building. */
export const ORG_ROLE_PERMISSIONS: Record<OrgRole, readonly Permission[]> = {
  owner: [...ADMIN, ...BILLING],
  staff: STAFF_ESSENTIALS,
};

/** A facility `admin` runs that one building: the administrator's set there and nowhere else.
 * `staff` adds nothing itself; roles on the row say what they may do. */
export const FACILITY_ROLE_PERMISSIONS: Record<FacilityRole, readonly Permission[]> = {
  admin: ADMIN,
  staff: [],
};

export function isOrgRole(value: string): value is OrgRole {
  return (ORG_ROLES as readonly string[]).includes(value);
}

export function isFacilityRole(value: string): value is FacilityRole {
  return (FACILITY_ROLES as readonly string[]).includes(value);
}

/** Names a custom role may not take, any case or spacing: a role is given by writing its NAME onto
 * the membership, so "owner" or "admin" would be read as the built-in. */
const RESERVED_ROLE_NAMES = new Set<string>([
  ...ORG_ROLES,
  ...FACILITY_ROLES,
  // The facility role that went: a role named after it would read as a comeback.
  "manager",
  "member",
  "platform_admin",
]);

export function isReservedRoleName(name: string): boolean {
  return RESERVED_ROLE_NAMES.has(
    name
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_"),
  );
}
